//#region Util Imports

import * as _ from 'underscore'
import * as $ from 'jquery'

//#endregion

import { ColumnModel } from '../../modules/api/api.models'

import { Enums } from '../../models/common'
import { OpView, OpViewColumn, OpRoute, OpTask } from '../../models/core'
import { GetViewColumnOptions } from '../../models/core/op-view'
import { OpMarker } from '../../models/custom'

import { WhereClauseHelper, ConfigHelper } from '../../helpers'
import { SequentialItemSaver, SequentialItemSaverProgress, SequentialItemSaverResult, SequentialItemSaverMode } from '../../helpers/sequential-item-saver'
import { ICancellationToken } from '../../helpers/cancellation-token'

import { MAP_SERVICE_NAME, MapService } from '../../modules/common/map2'
import { GridDataSourceParserDelegate } from '../../modules/common/grid'

import { GridService, GRID_SERVICE_NAME } from '../grid'
import { SystemService, SYSTEM_SERVICE_NAME } from '../system'
import { USER_SESSION_SERVICE_NAME, UserSessionService } from '../user-session'

import { RouteService, ROUTE_SERVICE_NAME } from './route.service'


//#region Public Types

export const ROUTE_POINTS_SERVICE_NAME = 'RoutePointsService'
export const ROUTE_POINTS_SERVICE_PROVIDER_NAME = 'RoutePointsServiceProvider'

//#endregion


export class RoutePointService {

    //#region Costs

    public static get LOG_PREFIX(): string { return '[SIMAX.Services.Route.Points]' }

    //#endregion


    //#region Ctor

    constructor(
        private gridService: GridService,
        private mapService: MapService,
        private routeService: RouteService,
        private systemService: SystemService,
        private userSessionService: UserSessionService
    ) { }

    //#endregion


    //#region Columns

    public getPointsGridColumns(route: OpRoute): JQueryPromise<ColumnModel[]> {
        return this.gridService.getColumnModelByCategory(Enums.SimaxDataTypeGroupCategory.WEBSIMAX__MODULE_NAME__TASK_GRID_PARAMS, RouteService.SIMAX_MODULE_NAME, {
            groupReferenceType: Enums.ReferenceType.IdDistributor,
            groupReferenceValue: route.idDistributor,
            viewReferenceType: Enums.ReferenceType.IdTask
        })
    }

    public getPointsEditGridColumns(route: OpRoute): JQueryPromise<ColumnModel[]> {
        return this.gridService.getColumnModelByCategory(Enums.SimaxDataTypeGroupCategory.WEBSIMAX__MODULE_NAME__LOCATION_GRID_PARAMS, RouteService.SIMAX_MODULE_NAME, {
            groupReferenceType: Enums.ReferenceType.IdDistributor,
            groupReferenceValue: route.idDistributor,
            viewReferenceType: Enums.ReferenceType.IdLocation
        })
    }

    //#endregion


    //#region Edit

    public getPointGridEditObjectsFromRoute(route: OpRoute): any[] {
        return _.map(route.taskList, (task: OpTask) => {
            const gridObject: any = this.gridService.gridObjectFromIOpObject(task)

            gridObject.id = task.idLocation
            gridObject.idTask = task.idTask

            return gridObject
        })
    }

    public getPointGridEditDataSourceParser(locationView: OpView): GridDataSourceParserDelegate {
        return (route: OpRoute) => {
            const routeMarker: OpMarker = this.mapService.createOpMarker(route, {
                view: locationView,
            })

            const additionalFields = []

            if (routeMarker != null && routeMarker.coords != null) {
                additionalFields.push({
                    key: 'latitude',
                    value: routeMarker.coords.latitude
                }, {
                    key: 'longitude',
                    value: routeMarker.coords.longitude
                }, {
                    key: 'tooltip',
                    value: routeMarker.tooltip
                })
            }

            return {
                additionalFields,
            }
        }
    }

    //#endregion


    //#region Export

    public getPointsExportWhereClause(routeId: number): JQueryPromise<string> {
        if (routeId == null) {
            $.Deferred().resolve(null).promise()
        }

        return this.userSessionService
            .getDataProvider().View
            .getActiveView(Enums.ReferenceType.IdTask)
            .then((view: OpView) => {
                const options: GetViewColumnOptions = new GetViewColumnOptions(Enums.DataType.HELPER_ID_PLANNED_ROUTE)

                let viewColumn: OpViewColumn = null

                if (view != null) {
                    viewColumn = view.getViewColumn(options)
                }

                if (viewColumn != null) {
                    return WhereClauseHelper.appendIn(null, viewColumn.name, routeId)
                }

                return null
            })
    }

    //#endregion


    //#region Markers

    public getMarkersFromTasks(tasks: OpTask[]): JQueryPromise<OpMarker[]> {
        return this.userSessionService
            .getDataProvider().View
            .getActiveView(Enums.ReferenceType.IdTask)
            .then((taskView: OpView) => {
                if (taskView != null) {
                    return _
                        .chain(tasks)
                        .map((task: OpTask) => {
                            return this.mapService.createOpMarker(task, {
                                markerStringReferenceType: Enums.ReferenceType.IdLocation,
                                view: taskView,
                            })
                        })
                        .filter((marker: OpMarker) => {
                            return marker != null
                        })
                        .value()
                }

                return []
            })
    }

    //#endregion


    //#region Options

    public openTaskDetails(idTask: number, pageTaskIds: number[]): void {
        this.systemService.openDetails({
            moduleName: Enums.SimaxModuleName.Task,
            referenceObjectId: idTask,
            pageItemsIds: pageTaskIds
        })
    }

    //#endregion


    //#region Route

    public isIgnoredPointsOrderRoute(route: OpRoute): boolean {
        if (route != null) {
            let ignoredOrderRouteIdTypes: number[] = ConfigHelper.getValue(Enums.SimaxConfigSection.Route, ConfigHelper.Key.IgnoredPointsOrderForRouteTypes)

            if (_.size(ignoredOrderRouteIdTypes) === 0) {
                ignoredOrderRouteIdTypes = [Enums.RouteType.READOUT]
            }

            return _.contains(ignoredOrderRouteIdTypes, route.idRouteType)
        }

        return false
    }

    public getLocationView(): JQueryPromise<OpView> {
        return this.userSessionService.getDataProvider().View.getActiveView(Enums.ReferenceType.IdLocation)
    }

    //#endregion


    //#region Save

    public isPartSavingRequired(route: OpRoute, tasks: OpTask[]): boolean {
        const modifiedCount: number = _
            .chain(tasks)
            .filter((task: OpTask) => {
                if (task.opState === Enums.OpChangeState.New || task.opState === Enums.OpChangeState.Delete) {
                    return true
                }

                if (task.opState === Enums.OpChangeState.Modified && !this.isIgnoredPointsOrderRoute(route)) {
                    return true
                }
            })
            .size()
            .value()

        return modifiedCount > this.getRoutePointsBatchSize()
    }

    public saveTasksToRoute(sourceRoute: OpRoute, tasks: OpTask[], cancellationToken: ICancellationToken, progressFunc: (progress: number) => void): JQueryPromise<OpRoute> {
        const routeToSave: OpRoute = this.prepareRouteToSave(sourceRoute, tasks)
        const taskBatches: OpTask[][] = this.prepareTaskBatches(routeToSave.taskList)
        const routesToSave: OpRoute[] = this.prepareRouteBatch(sourceRoute, taskBatches)

        if (_.size(routesToSave) > 0) {
            return this.saveRouteBatch(
                routesToSave,
                cancellationToken,
                (savedRoute: OpRoute, batchIndex: number) => {
                    this.insertPreviousBatch(routesToSave[batchIndex + 1], routesToSave[batchIndex], savedRoute)

                    progressFunc((batchIndex + 1) / _.size(routesToSave) * 100)
                })
        }

        return $.Deferred().resolve(sourceRoute).promise()
    }

    private insertPreviousBatch(nextRoute: OpRoute, prevRoute: OpRoute, savedPrevRoute: OpRoute): void {
        if (prevRoute != null && nextRoute != null) {
            const prevTasks: OpTask[] = _
                .chain(prevRoute.taskList)
                .filter((prevTask: OpTask) => {
                    return prevTask.opState !== Enums.OpChangeState.Delete
                })
                .map((prevTask: OpTask) => {
                    const clonedTask: OpTask = prevTask.clone()

                    if (clonedTask.opState === Enums.OpChangeState.New) {
                        const savedTask: OpTask = _.find(savedPrevRoute.taskList, (savedPrevTask: OpTask) => savedPrevTask.idLocation === clonedTask.idLocation)

                        if (savedTask != null) {
                            clonedTask.idTask = savedTask.idTask
                        }
                    }

                    clonedTask.opState = Enums.OpChangeState.Loaded

                    return clonedTask
                })
                .value()

            nextRoute.taskList.unshift(...prevTasks)
        }
    }

    private getRoutePointsBatchSize(): number {
        return ConfigHelper.getValue(Enums.SimaxConfigSection.Route, ConfigHelper.Key.RoutePointsPartSavingCount)
    }

    private prepareRouteBatch(sourceRoute: OpRoute, taskBatches: OpTask[][]): OpRoute[] {
        return _.map(taskBatches, (taskBatch: OpTask[]) => {
            const clonedRoute: OpRoute = sourceRoute.clone()

            clonedRoute.taskList = taskBatch

            return clonedRoute
        })
    }

    private prepareRouteToSave(sourceRoute: OpRoute, tasks: OpTask[]): OpRoute {
        const routeToSave: OpRoute = sourceRoute.clone()

        routeToSave.taskList = _.map(tasks, (task: OpTask) => {
            const clonedTask: OpTask = task.clone()

            if (clonedTask.opState === Enums.OpChangeState.Modified && this.isIgnoredPointsOrderRoute(sourceRoute)) {
                clonedTask.opState = Enums.OpChangeState.Loaded
            }

            return clonedTask
        })

        return routeToSave
    }

    private prepareTaskBatches(tasksToSave: OpTask[]): OpTask[][] {
        const taskBatches: OpTask[][] = []
        const batchSize: number = this.getRoutePointsBatchSize()

        let modifiedBatchSize: number = 0
        let currentBatch: OpTask[] = []

        _.forEach(tasksToSave, (taskToSave: OpTask, index: number) => {
            if (taskToSave.opState != Enums.OpChangeState.Loaded) {
                // Loaded tasks are not included in batch size
                modifiedBatchSize += 1
            }

            currentBatch.push(taskToSave)

            if (modifiedBatchSize === batchSize || (modifiedBatchSize < batchSize && index === _.size(tasksToSave) - 1)) {
                taskBatches.push(currentBatch)
                modifiedBatchSize = 0
                currentBatch = []
            }
        })

        return taskBatches
    }

    private saveRouteBatch(routesToSave: OpRoute[], cancellationToken: ICancellationToken, progressFunc: (savedRoute: OpRoute, routesSaved: number) => void): JQueryPromise<OpRoute> {
        return new SequentialItemSaver({
            itemsToSave: routesToSave,
            mode: SequentialItemSaverMode.StopOnError,

            saveDelegate: (routeToSave: OpRoute) => {
                return this.routeService.saveRoute(routeToSave, true)
            },

            progressDelegate: (progress: SequentialItemSaverProgress<OpRoute>) => {
                progressFunc(progress.savedItem, progress.index)
            }
        })
            .start(cancellationToken)
            .then((result: SequentialItemSaverResult<OpRoute>) => {
                if (result.hasErrors) {
                    return $.Deferred().reject(_.first(result.errors))
                }

                return _.last(result.savedItems)
            })
    }

    //#endregion


    //#region Task

    public getTasksFromGridEditObjects(grid: any[]): OpTask[] {
        return _.map(grid, (gridItem: any) => {
            const task: OpTask = new OpTask(null)

            task.idLocation = gridItem.id

            if (gridItem.isNew) {
                task.opState = Enums.OpChangeState.New
            } else if (gridItem.isRemoved) {
                task.opState = Enums.OpChangeState.Delete
                task.idTask = gridItem.idTask
            } else if (gridItem.isEdited) {
                task.opState = Enums.OpChangeState.Modified
                task.idTask = gridItem.idTask
            } else {
                task.opState = Enums.OpChangeState.Loaded
                task.idTask = gridItem.idTask
            }

            return task
        })
    }

    //#endregion
}

export class RoutePointServiceProvider implements ng.IServiceProvider {

    //#region IServiceProvider Implementation 

    public $get = [
        GRID_SERVICE_NAME,
        MAP_SERVICE_NAME,
        ROUTE_SERVICE_NAME,
        SYSTEM_SERVICE_NAME,
        USER_SESSION_SERVICE_NAME,
        function (
            gridService: GridService,
            mapService: MapService,
            routeService: RouteService,
            systemService: SystemService,
            userSessionService: UserSessionService
        ) {
            return new RoutePointService(gridService, mapService, routeService, systemService, userSessionService)
        }]

    //#endregion
} 